import { useEffect } from 'react'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { CallAction, DeviceBtnType } from '~/types/YealinkTypes'
import { isElectron } from '~/utils/common'
import { getDisplayCallerInfo } from '~/utils/phoneHelpers'

import { type ElectronBridge, PhoneCallStatus } from '@mangovoice/types'

/** The ipcRenderer event handler for Yealink button presses */
function _yealinkButtonPressed(
  _event: unknown,
  btnType: DeviceBtnType,
  value: boolean,
) {
  console.log('☏ YealinkHeadset - btnPress received', btnType, value)
  _yealinkButtonHandler(btnType, value)
}

/** The ipcRenderer event handler for Yealink call actions */
function _yealinkCallAction(
  _event: unknown,
  cid: number,
  callAction: CallAction,
) {
  console.log('☏ YealinkHeadset - callAction received', cid, callAction)
  _yealinkCallActionHandler(cid, callAction)
}

function _yealinkDeviceAttached() {
  console.log('☏ YealinkHeadset - deviceAttached received')
  _yealinkDeviceAttachedHandler()
}

// Add event listeners for Yealink events
if (isElectron) {
  window.electron?.on('yealink-device-btn-press', _yealinkButtonPressed)
  window.electron?.on('yealink-device-call-action', _yealinkCallAction)
  window.electron?.on('yealink-device-attached', _yealinkDeviceAttached)
}

/** The internal handler for Yealink button presses so we don't have to keep registering/unregistering the event listener */
let _yealinkButtonHandler: (
  btnType: DeviceBtnType,
  value: boolean,
) => void = () => {}

/** The internal handler for Yealink call actions so we don't have to keep registering/unregistering the event listener */
let _yealinkCallActionHandler: (
  cid: number,
  callAction: CallAction,
) => void = () => {}

/** The internal handler for Yealink device attached so we don't have to keep registering/unregistering the event listener */
let _yealinkDeviceAttachedHandler: () => void = () => {}

/**
 * Convert a UUID to a 32-bit integer.
 * Yealink requires a call to have a unique identifier as a
 * 32 bit number instead of a UUID, so we are just converting the UUID to a number.
 * It has a small chance of collisions, but it should be negligible in our use case.
 * @param uuid The UUID to convert
 * @returns The 32-bit integer
 */
function convertUUIDToYealinkCallId(uuid: string): number
function convertUUIDToYealinkCallId(uuid: null | undefined): null
function convertUUIDToYealinkCallId(uuid: string | undefined): number | null
function convertUUIDToYealinkCallId(
  uuid: string | null | undefined,
): number | null {
  if (uuid === null || uuid === undefined) {
    return null
  }
  // Remove dashes and convert to hex chunks
  const cleanUuid = uuid.replace(/-/g, '')
  // Take parts of the UUID and XOR them together to get 32 bits
  const part1 = parseInt(cleanUuid.slice(0, 8), 16)
  const part2 = parseInt(cleanUuid.slice(8, 16), 16)
  const part3 = parseInt(cleanUuid.slice(16, 24), 16)
  const part4 = parseInt(cleanUuid.slice(24), 16)

  // XOR all parts together and use modulo to ensure it fits in 32-bit signed int range
  return Math.abs((part1 ^ part2 ^ part3 ^ part4) % 0x7fffffff)
}

// We need to keep track of what Yealink thinks the call state is
// so we can update it when the call state changes
enum YealinkCallState {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
  CONNECTED = 'CONNECTED',
  TERMINATED = 'TERMINATED',
  HOLD = 'HOLD',
  ANSWERING = 'ANSWERING',
}

interface YealinkCall {
  callId: number
  uuid: string
  callState: YealinkCallState
}

const yealinkCalls = new Map<number, YealinkCall>()

/**
 * The YealinkHeadset component for integrating Yealink headsets
 */
export const YealinkHeadset = ({ electron }: { electron: ElectronBridge }) => {
  const {
    state: {
      phoneCalls: {
        active: { activeCall },
        list: { callList: calls },
        invite: { pendingInvitation },
      },
    },
    actions: { answerCall, muteCall, endCall, rejectCall, holdCall },
  } = usePhoneEndpoint({})

  _yealinkDeviceAttachedHandler = () => {
    console.log('☏ YealinkHeadset - deviceAttached received')
    // TODO: Updated device to match current call state
  }

  useEffect(() => {
    for (const yealinkCall of yealinkCalls.values()) {
      if (yealinkCall.uuid === activeCall?.callUuid) {
        // We are only updating the non-active call statuses here
        continue
      }
      if (yealinkCall.callState === YealinkCallState.TERMINATED) {
        continue
      }
      const call = calls.get(yealinkCall.uuid)

      // If the call is ended or ending, terminate the Yealink call
      if (
        !call ||
        call.status === PhoneCallStatus.ENDED ||
        call.status === PhoneCallStatus.ENDING_CALL
      ) {
        //
        electron.yealinkDevice.callTerminated(yealinkCall.callId)
        yealinkCall.callState = YealinkCallState.TERMINATED
      }

      // Check if the call is on hold and Yealink needs to know
      else if (
        call.isOnHold &&
        yealinkCall.callState !== YealinkCallState.HOLD
      ) {
        // Tell Yealink the call is on hold
        const displayInfo = getDisplayCallerInfo(call, true)
        electron.yealinkDevice.callHoldChanged(true, yealinkCall.callId, {
          name: displayInfo.name ?? 'Unknown',
          number: displayInfo.number,
        })
        yealinkCall.callState = YealinkCallState.HOLD
      }
    }

    if (activeCall) {
      const callId = convertUUIDToYealinkCallId(activeCall.callUuid)
      const yealinkCall = yealinkCalls.get(callId)
      const displayInfo = getDisplayCallerInfo(activeCall, true)
      if (!yealinkCall) {
        // We didn't get a pending invitation, so this is an outbound call...
        electron.yealinkDevice.outboundCall(callId, {
          name: displayInfo.name ?? 'Unknown',
          number: displayInfo.number,
        })
        yealinkCalls.set(callId, {
          callId,
          uuid: activeCall.callUuid,
          callState: YealinkCallState.OUTGOING,
        })
      } else if (
        yealinkCall.callState === YealinkCallState.HOLD &&
        !activeCall.isOnHold
      ) {
        // The device thinks we are on hold, but the active call is not, so we need to tell Yealink the call is not on hold
        electron.yealinkDevice.callHoldChanged(false, callId, {
          name: displayInfo.name ?? 'Unknown',
          number: displayInfo.number,
        })
        yealinkCall.callState = YealinkCallState.CONNECTED
      } else if (
        yealinkCall.callState !== YealinkCallState.HOLD &&
        activeCall.isOnHold
      ) {
        // The device thinks we are not on hold, but the active call is, so we need to tell Yealink the call is on hold
        electron.yealinkDevice.callHoldChanged(true, callId, {
          name: displayInfo.name ?? 'Unknown',
          number: displayInfo.number,
        })
        yealinkCall.callState = YealinkCallState.HOLD
      } else if (
        yealinkCall.callState !== YealinkCallState.CONNECTED &&
        activeCall.status === PhoneCallStatus.CONNECTED
      ) {
        // The device thinks we are not connected, but the active call is, so we need to tell Yealink the call is connected
        electron.yealinkDevice.callStarted(callId)
        yealinkCall.callState = YealinkCallState.CONNECTED
      }
    }
  }, [activeCall?.number, activeCall?.status, activeCall?.isOnHold, calls.size])

  // Update yealink mute state to match the active call
  useEffect(() => {
    if (activeCall) {
      electron.yealinkDevice.callMuteChanged(
        activeCall.isMuted,
        convertUUIDToYealinkCallId(activeCall.callUuid),
      )
    }
  }, [activeCall?.callUuid, activeCall?.isMuted])

  useEffect(() => {
    if (!pendingInvitation) {
      // No pending invitation, check if there is a yealink incoming call still and terminate it
      for (const yealinkCall of yealinkCalls.values()) {
        // If the call is the active call, don't terminate it!
        if (
          yealinkCall.callState === YealinkCallState.INCOMING &&
          activeCall?.callUuid !== yealinkCall.uuid
        ) {
          console.log(
            `☏ YealinkHeadset - Terminating abandoned yealink incoming call: ${yealinkCall.callId}`,
          )
          electron.yealinkDevice.callRinging(false, yealinkCall.callId)
          electron.yealinkDevice.callTerminated(yealinkCall.callId)
          yealinkCall.callState = YealinkCallState.TERMINATED
        }
      }
      return
    }

    const callId = convertUUIDToYealinkCallId(pendingInvitation.callUuid)

    const yealinkCall = yealinkCalls.get(callId)
    if (!yealinkCall) {
      console.log(
        `☏ YealinkHeadset - No yealink call found for pending invitation, creating new yealink call: ${callId}, number: ${pendingInvitation?.number}`,
      )
      yealinkCalls.set(callId, {
        callId,
        uuid: pendingInvitation?.callUuid ?? 'Unknown',
        callState: YealinkCallState.INCOMING,
      })

      // Tell Yealink the call is ringing
      const displayInfo = getDisplayCallerInfo(pendingInvitation, true)
      electron.yealinkDevice.callRinging(true, callId)
      electron.yealinkDevice.incomingCall(callId, {
        name: displayInfo.name ?? 'Unknown',
        number: displayInfo.number,
      })
    }
  }, [pendingInvitation])

  /**
   * Handle Yealink button presses
   */
  useEffect(() => {
    const yealinkButtonHandler = (btnType: DeviceBtnType, value: boolean) => {
      console.log(
        `☏ YealinkHeadset - ${DeviceBtnType[btnType]} pressed with value: ${value}. NOT IMPLEMENTED YET`,
      )
    }

    _yealinkButtonHandler = yealinkButtonHandler

    return () => {
      _yealinkButtonHandler = () => {}
    }
  }, [])

  /**
   * Handle Yealink call actions
   */
  useEffect(() => {
    console.log(`☏ YealinkHeadset - useEffect - calls count: ${calls.size}`)
    const yealinkCallActionHandler = (cid: number, callAction: CallAction) => {
      const yealinkCall = yealinkCalls.get(cid)

      const attemptAnswerCall = () => {
        if (yealinkCall) {
          if (pendingInvitation?.callUuid === yealinkCall.uuid) {
            console.log(
              `☏ YealinkHeadset - Accepting pending invitation: ${pendingInvitation.number}, cid: ${cid}.`,
            )
            answerCall()
            yealinkCall.callState = YealinkCallState.ANSWERING
          } else {
            console.log(
              `☏ YealinkHeadset - Call already in progress so do nothing, cid: ${cid}.`,
            )
          }
        }
      }

      switch (callAction) {
        case CallAction.CALL_ACTION_CALL_OUT:
          if (cid === -1) {
            console.log(
              `☏ YealinkHeadset - Go to outbound call screen, cid: ${cid}.`,
            )
          } else {
            attemptAnswerCall()
          }
          break
        case CallAction.CALL_ACTION_ACCEPT:
          attemptAnswerCall()
          break
        case CallAction.CALL_ACTION_REJECT:
          if (yealinkCall) {
            if (pendingInvitation?.callUuid === yealinkCall.uuid) {
              console.log(
                `☏ YealinkHeadset - Rejecting pending invitation: ${pendingInvitation.number}, cid: ${cid}.`,
              )
              rejectCall()
            } else {
              console.log(
                `☏ YealinkHeadset - Pending invite rejection didn't match yealink call uuid, cid: ${cid}.`,
              )
            }
          }
          break
        case CallAction.CALL_ACTION_TERMINATE:
          if (yealinkCall) {
            console.log(`☏ YealinkHeadset - Terminating Call: ${cid}`)
            endCall(yealinkCall.uuid)
          } else {
            console.log(
              `☏ YealinkHeadset - No call to reject so ignoring terminate pressed: ${cid}.`,
            )
          }
          break
        case CallAction.CALL_ACTION_HOLD:
          console.log(`☏ YealinkHeadset - Held Call: ${cid}`)
          if (yealinkCall) {
            console.log(
              `☏ YealinkHeadset - Attempting to hold call: ${JSON.stringify(yealinkCall)}`,
            )
            holdCall(yealinkCall.uuid, true)
          }
          break
        case CallAction.CALL_ACTION_RESUME:
          console.log(`☏ YealinkHeadset - Resumed Call: ${cid}`)
          if (yealinkCall) {
            console.log(
              `☏ YealinkHeadset - Attempting to resume call: ${JSON.stringify(yealinkCall)}`,
            )
            holdCall(yealinkCall.uuid, false)
          }
          break
        case CallAction.CALL_ACTION_MUTE:
          console.log(`☏ YealinkHeadset - Muted Call: ${cid}`)
          if (activeCall) {
            console.log(
              `☏ YealinkHeadset - Attempting to mute active call: ${JSON.stringify(activeCall)}`,
            )
            muteCall(activeCall.callUuid, true)
          }
          break
        case CallAction.CALL_ACTION_UNMUTE:
          console.log(`☏ YealinkHeadset - Unmuted Call: ${cid}`)
          if (activeCall) {
            muteCall(activeCall.callUuid, false)
          }
          break
        default:
          console.log(
            `☏ YealinkHeadset - Unhandled Call Action: ${cid} ${CallAction[callAction]} ${callAction}`,
          )
      }
    }

    _yealinkCallActionHandler = yealinkCallActionHandler

    return () => {
      _yealinkCallActionHandler = () => {}
    }
  }, [activeCall?.callUuid, pendingInvitation, calls])

  return null
}
